import { useState } from "react";
import { Code2, Database, Camera, Sparkles, Info, Copy, LayoutTemplate, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DuplicateDialog from "@/components/duplicate/DuplicateDialog";
import SaveTemplateDialog from "@/components/templates/SaveTemplateDialog";
import { PanelHeader } from "./PagesPanel";
import ProjectAiCosts from "./ProjectAiCosts";
import GenerationReport from "./GenerationReport";
import CompositionCheck from "./CompositionCheck";
import ImagePlanPanel from "./ImagePlanPanel";
import { useEditor } from "./useEditorState";
import ProjectTypes from "./ProjectTypes";
import { useGenerateFlow } from "./GenerateFlow";
import FaviconSetting from "./FaviconSetting";

const OVERRIDE_KEYS = ["background", "text_color", "box_bg", "box_text", "button_bg", "button_text"];
// Section colours (Proprietà › Stile) are applied on top of the palette: say where, so a palette change without visible effect is explained.
function PaletteOverrides() {
  const { content, pageId } = useEditor();
  const page = content.pages.find((p) => p.id === pageId);
  const list = (page?.sections || []).filter((s) => OVERRIDE_KEYS.some((k) => s.style?.[k]));
  return (
    <p className="mt-2 text-[11px] leading-snug text-slate-500" data-testid="palette-overrides-note">
      I colori personalizzati delle sezioni (Proprietà › Stile e spaziatura) prevalgono sulla palette.
      {list.length > 0 && <> In questa pagina: <b className="text-slate-700" data-testid="palette-overrides-list">{list.map((s) => s.name).join(", ")}</b>; per tornare alla palette usa «reset» sui loro colori.</>}
    </p>);
}
import { COLOR_TOKENS, FONTS, LANGUAGES } from "@/lib/constants";

const RADII = [["none", "Nessuno"], ["sm", "Piccolo"], ["md", "Medio"], ["lg", "Grande"], ["xl", "Molto grande"], ["2xl", "Extra"], ["full", "Pillola"]];

function FontSelect({ value, onChange, testId }) {
  const list = FONTS.includes(value) ? FONTS : [value, ...FONTS].filter(Boolean);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-xs" data-testid={testId}><SelectValue placeholder="Font" /></SelectTrigger>
      <SelectContent className="max-h-72">{list.map((f) => <SelectItem key={f} value={f}><span style={{ fontFamily: f }}>{f}</span></SelectItem>)}</SelectContent>
    </Select>
  );
}

function AutoNavSetting() {
  const ed = useEditor();
  const c = ed.content;
  const own = (c.components || []).find((x) => x.placement === "header");
  const nav = c.nav || {};
  return (
    <div className="space-y-2" data-testid="autonav-setting">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">Menu del sito</Label>
      {own ? <p className="text-[11px] text-slate-500" data-testid="autonav-own-header">Il sito ha già un header proprio (componente «{own.name}»): il menu automatico non viene mostrato.</p> : (
        <>
          <label className="flex items-center justify-between gap-2 text-xs text-slate-700">Menu automatico in alto
            <Switch checked={nav.auto !== false} onCheckedChange={(v) => ed.setNav({ auto: v })} data-testid="autonav-switch" /></label>
          {nav.auto !== false && <Input value={nav.brand || ""} placeholder={ed.project.company_name || "Nome nel menu"} maxLength={60} onChange={(e) => ed.setNav({ brand: e.target.value })} className="h-8 text-sm" aria-label="Nome mostrato nel menu" data-testid="autonav-brand" />}
          <p className="text-[11px] text-slate-500">Mostra le pagine con «Mostra nel menu», con la loro etichetta e nell'ordine del pannello Pagine; su telefono diventa un menu a scomparsa. Non compare se la pagina ha già una sezione header.</p>
        </>)}
    </div>);
}

export function WhatsAppSetting() {
  const ed = useEditor();
  const w = ed.content.whatsapp || {};
  const phone = (w.phone || "").trim();
  const bad = phone && !/^\+\d{8,15}$/.test(phone.replace(/[\s.-]/g, ""));
  return (
    <div className="space-y-2" data-testid="wa-setting">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">Pulsante WhatsApp</Label>
      <label className="flex items-center justify-between gap-2 text-xs text-slate-700">Bolla WhatsApp in basso a destra
        <Switch checked={!!w.enabled && !!phone && !bad} disabled={!phone || bad} onCheckedChange={(v) => ed.setWhatsapp({ enabled: v })} data-testid="wa-switch" /></label>
      <Input value={w.phone || ""} placeholder="+39 333 1234567" onChange={(e) => ed.setWhatsapp({ phone: e.target.value })} onBlur={(e) => ed.setWhatsapp({ phone: e.target.value.replace(/[\s.-]/g, "") })} className="h-8 text-sm" aria-label="Numero WhatsApp" data-testid="wa-phone" />
      {bad && <p className="text-[11px] text-amber-700" data-testid="wa-phone-error">Scrivi il numero con il prefisso internazionale, es. +39 333 1234567.</p>}
      <Input value={w.message || ""} maxLength={300} placeholder="Messaggio precompilato (facoltativo)" onChange={(e) => ed.setWhatsapp({ message: e.target.value })} className="h-8 text-sm" aria-label="Messaggio WhatsApp precompilato" data-testid="wa-message" />
      <p className="text-[11px] text-slate-500">Spenta di default. Il numero arriva da «Contatti, social e link» del brief; qui puoi cambiarlo per questo sito.</p>
    </div>);
}

export default function ProjectPanel({ onCapture }) {
  const ed = useEditor();
  const { content, project, setUi, setSelection } = ed;
  const d = content.design;
  const [dialog, setDialog] = useState(null);
  const flow = useGenerateFlow();
  const setColor = (k, v) => ed.updateDesign((x) => { x.colors[k] = v; }, `design:color:${k}`);
  const openCode = (type) => { setSelection({ kind: type, id: type }); setUi({ rightTab: "code", rightOpen: true }); };
  return (
    <div className="pb-6" data-testid="project-panel">
      <PanelHeader title="Progetto" subtitle="Design e impostazioni" />
      <div className="px-4 space-y-5">
        <ProjectTypes />
        <AutoNavSetting />
        <WhatsAppSetting />
        <FaviconSetting />
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Palette</Label>
          <div className="mt-2 space-y-1.5">
            {COLOR_TOKENS.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2">
                <input type="color" value={/^#[0-9a-f]{6}$/i.test(d.colors?.[key] || "") ? d.colors[key] : "#000000"} onChange={(e) => setColor(key, e.target.value)} className="h-7 w-8 rounded border p-0.5 cursor-pointer" data-testid={`design-color-${key}`} />
                <span className="text-xs text-slate-700 flex-1">{label}</span>
                <Input value={d.colors?.[key] || ""} onChange={(e) => setColor(key, e.target.value)} className="h-7 w-24 text-[11px] font-mono" />
              </div>
            ))}
          </div>
          <PaletteOverrides />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Tipografia</Label>
          <p className="text-[11px] text-slate-500">Titoli</p>
          <FontSelect value={d.fonts?.heading} onChange={(v) => ed.updateDesign((x) => { x.fonts.heading = v; }, null)} testId="design-font-heading" />
          <p className="text-[11px] text-slate-500">Testo</p>
          <FontSelect value={d.fonts?.body} onChange={(v) => ed.updateDesign((x) => { x.fonts.body = v; }, null)} testId="design-font-body" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[11px] text-slate-500 mb-1">Arrotondamento</p>
            <Select value={d.radius || "lg"} onValueChange={(v) => ed.updateDesign((x) => { x.radius = v; }, null)}>
              <SelectTrigger className="h-8 text-xs" data-testid="design-radius-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RADII.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 mb-1">Lingua demo</p>
            <Select value={content.language || "it"} onValueChange={ed.setLanguage}>
              <SelectTrigger className="h-8 text-xs" data-testid="design-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <Button variant="outline" size="sm" className="justify-start" onClick={() => openCode("css")} data-testid="edit-global-css-btn"><Code2 size={14} className="mr-2" />CSS globale</Button>
          <Button variant="outline" size="sm" className="justify-start" onClick={() => openCode("data")} data-testid="edit-data-btn"><Database size={14} className="mr-2" />Dati simulati (JSON)</Button>
          <Button variant="outline" size="sm" className="justify-start" onClick={onCapture} title="Aggiorna la miniatura del progetto dalla home" data-testid="refresh-thumbnail-btn"><Camera size={14} className="mr-2" />Aggiorna miniatura</Button>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <Button variant="outline" size="sm" className="justify-start" onClick={() => ed.save().then(() => setDialog("dup"))} title="Duplica per un altro cliente (semplice o con AI)" data-testid="project-duplicate-btn"><Copy size={14} className="mr-2" />Duplica sito…</Button>
          {project.is_template
            ? <p className="text-[11px] text-slate-500 px-1" data-testid="project-is-template">Questo è un template</p>
            : <Button variant="outline" size="sm" className="justify-start" onClick={() => ed.save().then(() => setDialog("tpl"))} data-testid="project-save-template-btn"><LayoutTemplate size={14} className="mr-2" />Salva come template…</Button>}
        </div>
        {content.notes?.length > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="ai-assumptions">
            <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5"><Info size={13} />Informazioni ipotizzate dall'AI</p>
            <ul className="mt-2 space-y-1 list-disc pl-4 text-[11px] text-amber-900/90">{content.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
          </div>
        )}
        <GenerationReport />
        <CompositionCheck />
        <ImagePlanPanel />
        <ProjectAiCosts />
        <div className="flex flex-col gap-2">
          <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => flow.open("brief")} data-testid="panel-edit-brief-btn"><FileText size={14} className="mr-1.5 shrink-0" /><span className="truncate">Modifica brief</span></Button>
          <Button variant="outline" size="sm" className="w-full justify-start text-sky-700 border-sky-200 hover:bg-sky-50" onClick={() => flow.open("gen")} data-testid="regenerate-btn"><Sparkles size={14} className="mr-1.5 shrink-0" /><span className="truncate">Genera sito completo</span></Button>
        </div>
        {flow.dialogs}
      </div>
      <DuplicateDialog source={project} open={dialog === "dup"} onOpenChange={(o) => !o && setDialog(null)} />
      <SaveTemplateDialog project={project} open={dialog === "tpl"} onOpenChange={(o) => !o && setDialog(null)} />
    </div>
  );
}
